import {
  WSActions,
  WSAlert,
  WSCard,
  WSControl,
  WSEmptyState,
  WSInfoBox,
  WSList,
  WSProgressBar,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  InsuranceCoverage,
  InsuranceImportStatus
} from "@wingspanhq/wingspan-api-sdk";
import { useCallback, useEffect, useState } from "react";

import { WSQueries } from "../../../../query/WSQuery";
import { StepComponent } from "../../../../shared/components/ModalFlow/types";
import { ErrorAlert } from "../../../../v3/components/ErrorAlert";
import { queriesInsuranceImports } from "../../../../v3/query/queriesInsuranceImports";
import { queriesVaultFiles } from "../../../../v3/query/queriesVaultFiles";
import { ValidationProgress } from "../../modules/SharedFile/components/ValidationProgress";
import { selectorInsuranceCoverageIssues } from "../../selectors/selectorInsuranceCoverageIssues";
import { selectorInsuranceImportCoverages } from "../../selectors/selectorInsuranceImportCoverages";
import { selectorInsuranceImportValidationItems } from "../../selectors/selectorInsuranceImportValidationItems";
import { RequirementContextType } from "../../types";
import { AIValidationWidget } from "../AIValidationWidget";
import { ValidationItemProps } from "../AIValidationWidget/ValidationItem";

import { Policies } from "./Policies";
import { FlowUploadDocumentProps } from "./types";
import { useModalUploadFile } from "./useModalUploadFile";

/**
 * An import is `Created` until the backend picks it up, so both states are the
 * same thing to the payee: the document is in flight and the step keeps polling.
 */
const PROCESSING_STATUSES: InsuranceImportStatus[] = [
  InsuranceImportStatus.CREATED,
  InsuranceImportStatus.PROCESSING
];

const isProcessingStatus = (status?: InsuranceImportStatus) =>
  !!status && PROCESSING_STATUSES.includes(status);

const NoFile: React.FC<{ onUpload: () => void }> = ({ onUpload }) => {
  return (
    <WSList gap="2XL">
      <WSCard header={{ label: "Upload document" }}>
        <WSEmptyState
          actions={{
            buttons: [
              {
                label: "Upload document",
                onClick: onUpload
              }
            ]
          }}
          description="Upload a document to import and process as insurance policies."
          orientation="horizontal"
          theme="neutral"
          title="Upload your insurance document"
          type="documents"
        />
      </WSCard>
    </WSList>
  );
};

const Uploading: React.FC<{
  fileName: string;
}> = ({ fileName }) => {
  return (
    <WSList gap="L">
      <WSCard
        header={{
          label: fileName,
          value: {
            pill: {
              text: "Uploading",
              theme: "neutral"
            }
          }
        }}
      >
        <WSInfoBox
          icon="loader"
          iconColor="blue400"
          theme="neutral"
          transparent
        >
          A document is uploading. Please, do not close this page.
        </WSInfoBox>
      </WSCard>
    </WSList>
  );
};

const ErrorUploading: React.FC<{ fileName: string }> = ({ fileName }) => {
  return (
    <WSList gap="L">
      <WSAlert icon="alert-circle" theme="error" title="Upload failed">
        Something went wrong with the upload. Please try the upload again or
        upload a new file. If the issue persists, please contact support.
      </WSAlert>
      <WSCard
        header={{
          label: fileName,
          value: {
            pill: { text: "Upload failed", theme: "error" }
          }
        }}
      >
        <WSProgressBar
          my="L"
          percent={100}
          text={
            <WSInfoBox icon="alert-circle" mt="S" theme="warning" transparent>
              There was an error uploading the file
            </WSInfoBox>
          }
          theme="error"
        />
      </WSCard>
    </WSList>
  );
};

const Validations: React.FC<{ items: ValidationItemProps[] }> = ({ items }) => (
  <AIValidationWidget
    contextType={RequirementContextType.Payer}
    hideTitle
    validationItems={items}
  />
);

const Processing: React.FC<{
  fileName: string;
  validationItems: ValidationItemProps[];
}> = ({ fileName, validationItems }) => {
  return (
    <WSList gap="L">
      <WSCard
        header={{
          label: fileName,
          value: {
            pill: {
              text: "Validating",
              theme: "neutral"
            }
          }
        }}
      >
        <ValidationProgress
          isBackgroundValidation={false}
          jobs={validationItems}
        />

        <WSInfoBox mt="L">
          Do not close or exit the window until the validation is finalized or
          you will have to start the process over.
        </WSInfoBox>
      </WSCard>

      <Validations items={validationItems} />
    </WSList>
  );
};

const Importing: React.FC<{
  fileName: string;
  coverages: InsuranceCoverage[];
}> = ({ fileName, coverages }) => {
  return (
    <WSList gap="L">
      <WSCard
        header={{
          label: fileName,
          value: {
            pill: {
              text: "Awaiting import",
              theme: "warning"
            }
          }
        }}
      >
        <WSList gap="L">
          <WSInfoBox color="gray500" icon="file" mt="S" transparent>
            A document is uploaded and ready to be imported and processed as
            insurance policies.
          </WSInfoBox>

          <WSAlert icon="bulb" theme="info" title="Pro tip">
            Review the extracted data and policies below to check that all
            information is accurate prior to completing the import process. If
            there are any inaccuracies or the wrong file was uploaded, upload a
            new file a try again.
          </WSAlert>
        </WSList>
      </WSCard>

      <Policies coverages={coverages} />
    </WSList>
  );
};

const InvalidDocument: React.FC<{
  fileName: string;
  validationItems: ValidationItemProps[];
}> = ({ fileName, validationItems }) => {
  return (
    <WSList gap="L">
      <WSAlert icon="alert-circle" theme="error" title="Invalid document">
        The document that was uploaded is invalid or illegible and could not be
        processed, please check the document and try again or upload a new file.
      </WSAlert>

      <WSCard
        header={{
          label: fileName,
          value: {
            pill: {
              icon: "alert-circle",
              text: "Invalid document",
              theme: "error"
            }
          }
        }}
      >
        <WSProgressBar my="L" percent={100} theme="error" />

        <WSInfoBox icon="time-five" theme="neutral" transparent>
          Invalid document uploaded
        </WSInfoBox>
      </WSCard>

      <Validations items={validationItems} />
    </WSList>
  );
};

export const Step2: StepComponent<FlowUploadDocumentProps> = ({
  state,
  setState,
  onNext,
  goToStep,
  goToSideStep,
  onBack,
  children,
  onClose
}) => {
  const { fileId, insuranceImportId } = state;

  const [certified, setCertified] = useState(false);

  const uploadDocument = useModalUploadFile();

  const queryVaultFile = queriesVaultFiles.getVaultFile.useQuery(
    { fileId: fileId! },
    { enabled: !!fileId }
  );

  const [createInsuranceImport, createInsuranceImportMeta] =
    queriesInsuranceImports.createInsuranceImport.useMutation();

  const [updateInsuranceImport, updateInsuranceImportMeta] =
    queriesInsuranceImports.updateInsuranceImport.useMutation();

  const queryInsuranceImport =
    queriesInsuranceImports.getInsuranceImport.useQuery(
      { importId: insuranceImportId! },
      {
        enabled: !!insuranceImportId && !!fileId,
        refetchInterval: result =>
          isProcessingStatus(result?.status) ? 3000 : false
      }
    );

  const onUploadNewFile = useCallback(() => {
    uploadDocument.open({
      onSuccess(file) {
        // both mutations are reset: a replacement file retries whichever of the
        // two failed, and a create error left standing would keep the step on
        // its error state no matter what was uploaded next
        createInsuranceImportMeta.reset();
        updateInsuranceImportMeta.reset();

        queryInsuranceImport.remove();
        queryVaultFile.remove();
        setState({
          fileId: file.fileId
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createInsuranceImportMeta.reset, updateInsuranceImportMeta.reset]);

  useEffect(() => {
    if (!insuranceImportId && fileId) {
      createInsuranceImport(
        { insuranceImportCreate: { fileId } },
        {
          onSuccess: response => {
            setState({
              insuranceImportId: response.id
            });
          }
        }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insuranceImportId, fileId]);

  useEffect(() => {
    if (!fileId) {
      onUploadNewFile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileId]);

  // the import is created carrying the current file, so this only fires when
  // the payee replaces it from the review step
  useEffect(() => {
    if (
      fileId &&
      insuranceImportId &&
      queryInsuranceImport.isSuccess &&
      queryInsuranceImport.data?.fileId !== fileId
    ) {
      updateInsuranceImport({
        importId: insuranceImportId,
        insuranceImportUpdate: {
          fileId
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fileId,
    insuranceImportId,
    queryInsuranceImport.isSuccess,
    queryInsuranceImport.data?.fileId
  ]);

  if (!fileId) {
    return <NoFile onUpload={onUploadNewFile} />;
  }

  // A failed create leaves no import id — and the import read below is keyed on
  // exactly that id, so it can never be enabled and the query gate would wait
  // on it forever. The error has to be rendered before the gate or it is
  // unreachable, which is how a rejected create came to read as a blank step.
  if (createInsuranceImportMeta.error) {
    return (
      <WSList gap="2XL">
        <ErrorUploading
          fileName={
            queryVaultFile.data?.filename ||
            queryVaultFile.data?.title ||
            "Document"
          }
        />

        <ErrorAlert
          context="UploadFile"
          error={createInsuranceImportMeta.error}
        />

        <WSActions
          alignment="fill"
          buttons={[
            {
              label: "Cancel",
              onClick: onClose,
              kind: "Secondary"
            },
            {
              label: "Upload new file",
              onAsyncClick: async () => {
                onUploadNewFile();
              },
              kind: "Primary"
            }
          ]}
        />
      </WSList>
    );
  }

  return (
    <WSQueries queries={{ queryVaultFile, queryInsuranceImport }}>
      {({ queryVaultFileData, queryInsuranceImportData }) => {
        // both the filename and the title are optional on a vault file, so the
        // card needs a last-resort label
        const fileName =
          queryVaultFileData.filename || queryVaultFileData.title || "Document";

        const isProcessing = isProcessingStatus(
          queryInsuranceImportData.status
        );

        const isReadyToSubmit =
          queryInsuranceImportData.status ===
          InsuranceImportStatus.READY_FOR_REVIEW;

        // TODO(v3-verify): v1's "Rejected" status has no listed v3 equivalent
        // (InsuranceImportStatus is Created/Processing/ReadyForReview/
        // Complete/Failed) — mapped to FAILED as the closest fit. Confirm with
        // BE whether a rejected-but-retryable state still exists under a
        // different name.
        const isInvalidDocument =
          queryInsuranceImportData.status === InsuranceImportStatus.FAILED;

        const isError =
          !!createInsuranceImportMeta.error ||
          !!updateInsuranceImportMeta.error;

        // a failed attach leaves the import pointing at the previous file, so
        // the mismatch below outlives the error — the error has to win, or the
        // step sits on the uploading spinner forever
        const isUploading =
          queryVaultFile.isLoading ||
          updateInsuranceImportMeta.isLoading ||
          createInsuranceImportMeta.isLoading ||
          queryInsuranceImportData.fileId !== fileId;

        const coverages = selectorInsuranceImportCoverages(
          queryInsuranceImportData
        );
        const importableCoverages = coverages.filter(
          coverage => selectorInsuranceCoverageIssues(coverage).length === 0
        );
        const validationItems = selectorInsuranceImportValidationItems(
          queryInsuranceImportData
        );

        return (
          <WSList gap="2XL">
            {isError ? (
              <ErrorUploading fileName={fileName} />
            ) : isUploading ? (
              <Uploading fileName={fileName} />
            ) : isInvalidDocument ? (
              <InvalidDocument
                fileName={fileName}
                validationItems={validationItems}
              />
            ) : isProcessing ? (
              <Processing
                fileName={fileName}
                validationItems={validationItems}
              />
            ) : isReadyToSubmit ? (
              <>
                <Importing coverages={coverages} fileName={fileName} />
                <WSCard>
                  <WSList gap="M">
                    <WSControl
                      label={
                        <WSText.ParagraphSm>
                          I certify that the Certificate of Insurance I am
                          uploading is a true, correct, and unaltered copy
                          issued by the insurer/producer; that the information
                          on it is accurate as of the date of submission; and
                          that I have reviewed and confirm the extracted fields
                          in this form match the certificate.
                        </WSText.ParagraphSm>
                      }
                      size="S"
                      type="checkbox"
                      value={certified}
                      onChange={setCertified}
                    />
                    <WSInfoBox>
                      The import action can't be rolled back, it will import the
                      policies as you see them here. Should the policies expire
                      or change in the future, you can upload new documents to
                      keep your profile up to date.
                    </WSInfoBox>
                  </WSList>
                </WSCard>
              </>
            ) : null}

            <ErrorAlert
              context="UploadFile"
              error={
                createInsuranceImportMeta.error ||
                updateInsuranceImportMeta.error
              }
            />

            <WSActions
              alignment="fill"
              buttons={[
                {
                  label: "Cancel",
                  visible: !isReadyToSubmit,
                  onClick: onClose,
                  kind: "Secondary"
                },
                {
                  label: "Upload new file",
                  disabled: !insuranceImportId || isProcessing,
                  onAsyncClick: async () => {
                    onUploadNewFile();
                  },
                  kind: isReadyToSubmit ? "Secondary" : "Primary"
                },
                {
                  label: "Import",
                  disabled:
                    !isReadyToSubmit ||
                    !certified ||
                    importableCoverages.length === 0,
                  visible: isReadyToSubmit,
                  kind: "Primary",
                  onClick: onNext
                }
              ]}
            />
          </WSList>
        );
      }}
    </WSQueries>
  );
};
